import React, { useCallback, useId } from 'react';

import SENSITIVE_DATA_CLASS from '@rover/constants/src/platform/sensitiveData.constants';
import useTheme from '@rover/kibble/themes/useTheme';

import { Box, Flex } from '../../../core';
import { FeedbackMessage, PrimaryLabel, SecondaryLabel } from '../sharedTypography';
import { getAriaDescribedByString } from '../utils';

import {
  CharacterCount,
  getBackgroundColor,
  getBorderColor,
  isTextareaVariantOptions,
  TextareaMinHeightType,
  TextFieldCommonProps,
  TextFieldIcon,
  WordCount,
} from './TextField.common';

type BaseWebTextFieldProps = TextFieldCommonProps & {
  /**
   * Space-separated ids of other elements that describe the input. Screen readers read them after
   * the error, helper message and secondary label.
   */
  describedBy?: string;
  /** The input's HTML `type`, which also picks the on-screen keyboard. Defaults to `text`. */
  inputType?: 'text' | 'email' | 'password' | 'url' | 'number';
  primaryLabelA11yHidden?: boolean;
  /** Called on each change, before the form stores the new value. */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Called when the input gains focus. */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Called when the input loses focus, before the form records it as touched. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Called when the input is clicked. */
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  /** Called when a key is pressed in the input. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Called when a key that types a character is pressed. The event is deprecated: prefer
   * `onKeyDown`.
   */
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

/** TextField's props on web. */
export type Props = BaseWebTextFieldProps;

/** A form field for a typed answer, on one line or several. */
const TextField = ({
  describedBy = '',
  disabled,
  fieldError,
  fieldProps,
  fieldWidth = '100%',
  helperMessage,
  icon,
  iconAlignment = 'right',
  marginBottom = '6x',
  placeholder,
  primaryLabel,
  primaryLabelA11yHidden,
  primaryLabelSize,
  readOnly,
  required = true,
  secondaryLabel,
  translatedFieldErrorMessage,
  inputType = 'text',
  onChange = () => {},
  variant = 'default',
  variantOptions = {},
  sensitive = false,
  onFocus = () => {},
  onBlur = () => {},
  onClick = () => {},
  onKeyDown = () => {},
  onKeyPress = () => {},
  webInputRef = () => {},
  requirednessLabel,
}: Props): JSX.Element => {
  const theme = useTheme();

  const primaryLabelId = useId();
  const fieldErrorId = useId();
  const helperMessageId = useId();
  const secondaryLabelId = useId();

  const describedByString = getAriaDescribedByString([
    [fieldError, fieldErrorId],
    [helperMessage, helperMessageId],
    [secondaryLabel, secondaryLabelId],
    [describedBy, describedBy],
  ]);

  const handleOnChange = useCallback(
    async (e) => {
      onChange(e);
      fieldProps.onChange(e);
    },
    [onChange, fieldProps]
  );

  const handleOnBlur = useCallback(
    async (e) => {
      onBlur(e);
      fieldProps.onBlur(e);
    },
    [onBlur, fieldProps]
  );

  const refCallback = useCallback(
    (el) => {
      fieldProps.ref(el);
      webInputRef(el);
    },
    [fieldProps, webInputRef]
  );

  let minHeight: TextareaMinHeightType = '12x';
  if (
    variant === 'textarea' &&
    isTextareaVariantOptions(variantOptions) &&
    variantOptions.minHeight
  ) {
    minHeight = variantOptions.minHeight;
  }

  const hasError: boolean = Boolean(fieldError || translatedFieldErrorMessage);

  const boxVariantProps =
    variant === 'textarea'
      ? ({ webAs: 'textarea' } as const)
      : ({
          webAs: 'input',
          type: inputType,
        } as const);

  return (
    <Flex flexDirection="column" mb={marginBottom} flex="1">
      <PrimaryLabel
        required={required}
        primaryLabel={primaryLabel}
        webInputId={primaryLabelId}
        primaryLabelA11yHidden={primaryLabelA11yHidden}
        primaryLabelSize={primaryLabelSize}
        requirednessLabel={requirednessLabel}
      />
      <SecondaryLabel secondaryLabel={secondaryLabel} labelId={secondaryLabelId} />
      <Flex
        flex="1"
        flexDirection="row"
        alignItems="center"
        position="relative"
        width={fieldWidth}
        data-testid="kff-text-input-flex-parent"
        className={sensitive ? SENSITIVE_DATA_CLASS : undefined}
      >
        <Box
          aria-describedby={describedByString || undefined} // pass undefined instead of empty string
          aria-disabled={disabled}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-required={required}
          {...boxVariantProps}
          border="2px solid"
          borderColor={getBorderColor(theme.colors, hasError, disabled)}
          borderRadius="secondary"
          color={
            disabled ? theme.colors.text.disabled.toString() : theme.colors.text.primary.toString()
          }
          data-testid="kff-text-input"
          id={primaryLabelId}
          minHeight={minHeight}
          paddingLeft={icon && iconAlignment === 'left' ? '8x' : '3x'}
          paddingRight={icon && iconAlignment === 'right' ? '8x' : '3x'}
          py="2x"
          width="100%"
          sx={{
            background: getBackgroundColor(theme, disabled, readOnly),
            fontSize: theme.text[200].fontSize,
            lineHeight: theme.paragraph[200].lineHeight,
            outlineOffset: '2px',
            pointerEvents: disabled ? 'none' : 'all',
          }}
          {...fieldProps}
          ref={refCallback}
          webAsProps={{
            onChange: handleOnChange,
            onFocus,
            onBlur: handleOnBlur,
            onKeyDown,
            onKeyPress,
            placeholder,
            readOnly: readOnly || disabled,
          }}
          onClick={onClick}
        />
        <TextFieldIcon
          icon={icon}
          iconAlignment={iconAlignment}
          disabled={disabled}
          textColor={theme.textColor}
        />
      </Flex>
      <CharacterCount variant={variant} variantOptions={variantOptions} />
      <WordCount variant={variant} variantOptions={variantOptions} />
      <FeedbackMessage
        fieldError={fieldError}
        fieldErrorId={fieldErrorId}
        helperMessage={helperMessage}
        helperMessageId={helperMessageId}
        translatedFieldErrorMessage={translatedFieldErrorMessage}
      />
    </Flex>
  );
};

export default TextField;
